// Stock Access Control — Live Stock Report IP allowlist manager.
//
// Super-admin only. Lists every IP that has either requested access,
// been approved, or been revoked, and exposes one-click approve/revoke/
// delete actions. Polls for fresh data every 20 s so a new request shows
// up in near real-time alongside the in-app banner.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Check, X, Trash2, RefreshCw, ShieldCheck, ShieldAlert, Clock, MonitorSmartphone, Search, MapPin, Phone, Building2, Globe, Pencil, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";
import { UserAccessOverview } from "./UserAccessOverview";
import { formatDateTime } from "../lib/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:  { label: "Pending",  cls: "bg-amber-100 text-amber-800",   icon: Clock },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800", icon: ShieldCheck },
  revoked:  { label: "Revoked",  cls: "bg-rose-100 text-rose-800",     icon: ShieldAlert },
};

const fmtDt = (iso) => formatDateTime(iso) || "—";

export const StockAccessControl = ({ authHeaders }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | approved | revoked
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/stock/access`, { headers: authHeaders });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast({ title: "Failed to load access list", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, toast]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const counts = useMemo(() => {
    const out = { pending: 0, approved: 0, revoked: 0 };
    rows.forEach(r => { out[r.status] = (out[r.status] || 0) + 1; });
    return out;
  }, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!q) return true;
      return (r.ip || "").toLowerCase().includes(q)
          || (r.requester_name || "").toLowerCase().includes(q)
          || (r.requester_company || "").toLowerCase().includes(q)
          || (r.requester_phone || "").toLowerCase().includes(q)
          || (r.requester_reason || "").toLowerCase().includes(q);
    });
  }, [rows, filter, query]);

  const act = async (id, op) => {
    setBusyId(id);
    try {
      if (op === "approve") {
        await axios.post(`${API}/admin/stock/access/${id}/approve`, {}, { headers: authHeaders });
      } else if (op === "revoke") {
        await axios.post(`${API}/admin/stock/access/${id}/revoke`, {}, { headers: authHeaders });
      } else if (op === "delete") {
        if (!window.confirm("Delete this IP record? The visitor will see the request form again on next visit.")) {
          setBusyId(null);
          return;
        }
        await axios.delete(`${API}/admin/stock/access/${id}`, { headers: authHeaders });
      }
      await load();
      toast({ title: `IP ${op}d` });
    } catch (e) {
      toast({ title: `Failed to ${op} IP`, description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditName(r.requester_name || "");
  };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };

  // Per-viewer item-ledger flag — lets the super admin switch the
  // expandable in/out ledger on/off for a specific approved viewer.
  const toggleLedger = async (id, enabled) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/stock/access/${id}/ledger`, { enabled }, { headers: authHeaders });
      await load();
      toast({ title: enabled ? "Item ledger enabled for this viewer" : "Item ledger disabled for this viewer" });
    } catch (e) {
      toast({ title: "Failed to update ledger access", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async (id) => {
    const trimmed = (editName || "").trim();
    if (!trimmed) { toast({ title: "Name cannot be empty", variant: "destructive" }); return; }
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/stock/access/${id}/name`, { name: trimmed }, { headers: authHeaders });
      setEditingId(null);
      setEditName("");
      await load();
      toast({ title: "Name updated" });
    } catch (e) {
      toast({ title: "Failed to rename", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="stock-access-control">
      {/* Header strip */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#0078D4]" />
              <h2 className="text-lg font-bold text-neutral-900">Access Control</h2>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Every visitor to your Live Stock Report needs your approval. Pending requests are emailed to you and show as an in-app banner.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="access-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Status chip filters */}
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { k: "all",      label: "All",      n: rows.length, cls: "bg-neutral-100 text-neutral-800" },
            { k: "pending",  label: "Pending",  n: counts.pending,  cls: STATUS_META.pending.cls },
            { k: "approved", label: "Approved", n: counts.approved, cls: STATUS_META.approved.cls },
            { k: "revoked",  label: "Revoked",  n: counts.revoked,  cls: STATUS_META.revoked.cls },
          ].map(c => (
            <button
              key={c.k}
              onClick={() => setFilter(c.k)}
              data-testid={`access-filter-${c.k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${filter === c.k ? `${c.cls} ring-2 ring-offset-1 ring-[#0078D4]/40` : `${c.cls} opacity-60 hover:opacity-100`}`}
            >
              {c.label} · {c.n}
            </button>
          ))}
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search IP, name, reason…"
              className="pl-9 h-9"
              data-testid="access-search"
            />
          </div>
        </div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="text-center text-neutral-400 py-12">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-center text-neutral-400 py-12 bg-white border border-dashed border-neutral-200 rounded-xl">
          No access records match the current filter.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100 overflow-hidden">
          {visible.map(r => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={r.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4" data-testid={`access-row-${r.ip}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className="font-mono text-sm text-neutral-900 font-semibold" data-testid={`access-ip-${r.ip}`}>{r.ip}</span>
                    {r.location?.country && r.location.country !== "ZZ" && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-neutral-700 bg-neutral-100 rounded-full px-2 py-0.5">
                        <Globe className="w-3 h-3" /> {r.location.country}
                      </span>
                    )}
                    {r.visit_count > 0 && (
                      <span className="text-[10px] text-neutral-500">· {r.visit_count} visit{r.visit_count === 1 ? "" : "s"}</span>
                    )}
                  </div>
                  <div className="mt-1.5 text-sm font-semibold text-neutral-800 truncate flex items-center gap-1.5">
                    {editingId === r.id ? (
                      <>
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(r.id); if (e.key === 'Escape') cancelEdit(); }}
                          autoFocus
                          className="h-7 px-2 text-sm font-semibold max-w-[280px]"
                          data-testid={`access-name-input-${r.ip}`}
                          placeholder="Identification label…"
                          maxLength={120}
                        />
                        <button
                          onClick={() => saveEdit(r.id)}
                          disabled={busyId === r.id}
                          className="p-1 rounded-md text-emerald-600 hover:bg-emerald-50"
                          data-testid={`access-name-save-${r.ip}`}
                          title="Save"
                        ><Save className="w-4 h-4" /></button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 rounded-md text-neutral-400 hover:bg-neutral-100"
                          data-testid={`access-name-cancel-${r.ip}`}
                          title="Cancel"
                        ><X className="w-4 h-4" /></button>
                      </>
                    ) : (
                      <>
                        <span data-testid={`access-name-${r.ip}`}>
                          {r.requester_name || <em className="text-neutral-400">No name provided</em>}
                        </span>
                        <button
                          onClick={() => startEdit(r)}
                          className="p-1 rounded-md text-neutral-400 hover:text-[#0078D4] hover:bg-neutral-100 transition opacity-60 hover:opacity-100"
                          data-testid={`access-name-edit-${r.ip}`}
                          title="Edit identification label"
                        ><Pencil className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                  {/* Company / phone strip — both forced fields */}
                  {(r.requester_company || r.requester_phone) && (
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-neutral-600 flex-wrap">
                      {r.requester_company && (
                        <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-neutral-400" />{r.requester_company}</span>
                      )}
                      {r.requester_phone && (
                        <a
                          href={`tel:${r.requester_phone}`}
                          className="inline-flex items-center gap-1 hover:text-[#0078D4] hover:underline"
                          data-testid={`access-phone-${r.ip}`}
                        >
                          <Phone className="w-3 h-3 text-neutral-400" />{r.requester_phone}
                        </a>
                      )}
                    </div>
                  )}
                  {r.requester_reason && (
                    <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{r.requester_reason}</div>
                  )}
                  {/* Device + Location row */}
                  <div className="mt-1.5 flex items-center gap-3 text-[11px] text-neutral-500 flex-wrap">
                    {r.device && (
                      <span className="inline-flex items-center gap-1">
                        <MonitorSmartphone className="w-3 h-3 text-neutral-400" />
                        {[r.device.device, r.device.os, r.device.browser].filter(Boolean).join(" · ")}
                      </span>
                    )}
                    {r.location?.lat && r.location?.lng ? (
                      <a
                        href={`https://maps.google.com/?q=${r.location.lat},${r.location.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#0078D4] hover:underline"
                        data-testid={`access-gmaps-${r.ip}`}
                        title="Open in Google Maps"
                      >
                        <MapPin className="w-3 h-3" />
                        {r.location.lat.toFixed(4)}, {r.location.lng.toFixed(4)}
                        {r.location.accuracy_m ? ` (±${Math.round(r.location.accuracy_m)}m)` : ""}
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-500">
                        <MapPin className="w-3 h-3" /> Location missing
                      </span>
                    )}
                    {r.location?.tz && <span>· {r.location.tz}</span>}
                  </div>
                  <div className="text-[11px] text-neutral-400 mt-1.5 flex items-center gap-3 flex-wrap">
                    <span>Requested {fmtDt(r.requested_at)}</span>
                    {r.approved_at && <span>· Approved {fmtDt(r.approved_at)}</span>}
                    {r.last_seen_at && <span>· Last seen {fmtDt(r.last_seen_at)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  {r.status === "approved" && (
                    <label
                      className={`inline-flex items-center gap-1.5 cursor-pointer rounded-full border px-2.5 py-1 ${r.ledger_enabled !== false ? 'border-[#0078D4]/40 bg-[#0078D4]/5' : 'border-neutral-200 bg-neutral-50'}`}
                      title="Allow this viewer to expand product rows and see the in/out ledger on the live report"
                    >
                      <input
                        type="checkbox"
                        checked={r.ledger_enabled !== false}
                        onChange={(e) => toggleLedger(r.id, e.target.checked)}
                        disabled={busyId === r.id}
                        className="w-3.5 h-3.5 accent-[#0078D4]"
                        data-testid={`access-ledger-toggle-${r.ip}`}
                      />
                      <span className={`text-[11px] font-semibold ${r.ledger_enabled !== false ? 'text-[#0078D4]' : 'text-neutral-500'}`}>
                        Item ledger
                      </span>
                    </label>
                  )}
                  {r.status !== "approved" && (
                    <Button
                      size="sm"
                      onClick={() => act(r.id, "approve")}
                      disabled={busyId === r.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`access-approve-${r.ip}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  )}
                  {r.status !== "revoked" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(r.id, "revoke")}
                      disabled={busyId === r.id}
                      data-testid={`access-revoke-${r.ip}`}
                    >
                      <X className="w-4 h-4 mr-1" /> Revoke
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => act(r.id, "delete")}
                    disabled={busyId === r.id}
                    className="text-neutral-400 hover:text-rose-600"
                    data-testid={`access-delete-${r.ip}`}
                    title="Delete record (visitor sees request form again)"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* User Access Overview — added 2026-07-21. Per-user × per-report
          grant table, searchable by name/email + filterable by report
          type. Shows both normal approvals and super-admin bypass. */}
      <UserAccessOverview authHeaders={authHeaders} />
    </div>
  );
};

export default StockAccessControl;
